/**
 * classifyMeetingAudience — decide whether a meeting is internal, external,
 * or indeterminate based on its attendees' email domains.
 *
 * "Internal" means every attendee shares the org's email domain (coworkers);
 * "external" means at least one attendee is outside it; "unknown" means we
 * can't prove either way and the UI should fall back to a neutral treatment.
 *
 * The org domain is derived from an anchor email — the current user's email,
 * the same anchor `isSameDomainAsOrg` uses elsewhere to dispatch "Add as user"
 * (coworker) vs "Add to CRM" (prospect). Freemail anchors (gmail.com, etc.)
 * can't identify an org domain, so they yield "unknown" rather than
 * misclassifying every personal-email attendee.
 *
 * Fail-closed: we only return "internal" when it's provable (≥1 attendee with
 * the org domain, and no attendee that is external OR has an unparseable
 * address). Anything ambiguous is "unknown", never "internal".
 */

import {
  domainOfEmail,
  isFreemailDomain,
} from '@/lib/crm/utils/isSameDomainAsOrg';
import type { CalendarEventGuest } from '@/pages/calendar/utils/eventMapper';

export type MeetingAudience = 'internal' | 'external' | 'unknown';

export function classifyMeetingAudience(
  guests: CalendarEventGuest[] | undefined,
  anchorEmail: string | null | undefined
): MeetingAudience {
  const anchorDomain = domainOfEmail(anchorEmail);
  // No usable org-domain anchor (missing, malformed, or a personal/freemail
  // address) — can't tell internal from external.
  if (!anchorDomain || isFreemailDomain(anchorDomain)) {
    return 'unknown';
  }
  if (!guests || guests.length === 0) {
    return 'unknown';
  }

  let sawInternal = false;
  let sawUnparseable = false;
  for (const guest of guests) {
    const guestDomain = domainOfEmail(guest.email);
    if (!guestDomain) {
      // Can't classify this attendee; it blocks a confident "internal" verdict
      // but isn't proof of "external" on its own.
      sawUnparseable = true;
      continue;
    }
    if (guestDomain !== anchorDomain) {
      // A single outside attendee makes the whole meeting external.
      return 'external';
    }
    sawInternal = true;
  }

  // No external attendee seen. Only call it internal when every attendee was
  // parseable and matched the org domain.
  if (sawInternal && !sawUnparseable) {
    return 'internal';
  }
  return 'unknown';
}
